import * as React from 'react'

import type { Popover as PopoverPrimitive } from '@base-ui/react/popover'
import { ChevronDown, Plus } from 'lucide-react'

import FullScreenPickerSheet, {
  SheetHeightMode,
} from '~/client/src/shared/components/FullScreenPickerSheet/FullScreenPickerSheet'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import { isEventFromOwnDom } from '~/client/src/shared/utils/isEventFromOwnDom'
import {
  isWithinAnchor,
  useCloseOnFocusLeavingAnchor,
} from '~/client/src/shared/utils/popoverAnchor'

import { cn } from '../../utils/cn'
import { computeVisibleTagCount } from './computeVisibleTagCount'
import { TagOverflowToggle } from './TagOverflowToggle'

const ROW_GAP_PX = 4

interface IProps {
  /** Whether the popover is open */
  isOpen: boolean
  /** Called when the trigger area is clicked */
  onOpen: () => void
  /** Called when the popover should close (click outside, escape, etc.) */
  onClose: () => void
  /** Placeholder text when no items are selected */
  placeholder: string
  /**
   * Accessible name of the affordance button, the picker's real trigger: what the
   * field edits or selects ("Permitted Companies", "Select company"). Not the
   * placeholder, which describes an empty state ("None", "Not set").
   */
  triggerLabel: string
  /** Popover content — the selector component */
  popoverContent: React.ReactNode
  /** Rendered tag elements — caller controls tag rendering */
  children?: React.ReactNode
  /**
   * A pinned leading element (e.g. the inherited-hours default chip) that always
   * renders first and never collapses behind the "+n more" toggle. It is measured
   * with the tags so overflow accounts for its width, but only the `children` tags
   * collapse — the leading node stays visible at any field width. Must render
   * exactly ONE root DOM node: overflow measurement maps each measured child's
   * width onto its slot positionally, so a multi-node leading (fragment/array)
   * would desync the width↔index alignment — the same one-node contract the tags
   * carry.
   */
  leading?: React.ReactNode
  /** Optional className on the popover content wrapper */
  popoverClassName?: string
  /**
   * Positioner collision handling. Left alone the popup flips above the trigger
   * when it outgrows the space below — right for a list that simply got longer,
   * wrong for an editor that changes size while open, where the flip reads as
   * the popup jumping away under the cursor.
   */
  popoverCollisionAvoidance?: React.ComponentProps<
    typeof PopoverContent
  >['collisionAvoidance']
  /** Where the popup sits relative to the cell. Defaults to below, right-aligned. */
  popoverSide?: React.ComponentProps<typeof PopoverContent>['side']
  popoverAlign?: React.ComponentProps<typeof PopoverContent>['align']
  /**
   * Field style. `bordered` (default) is a bordered, chevron-affixed input;
   * `cell` is borderless and fills its container (for table cells).
   */
  variant?: 'bordered' | 'cell'
  /** When set, tags wrapping beyond this many rows collapse behind a "+n more" chip */
  maxVisibleRows?: number
  /**
   * Affordance icon on the trigger button; defaults to a chevron. For the `cell`
   * variant it is right-aligned and shown on hover, on focus and while open. For
   * the `bordered` variant it is always shown. `chevron` for list pickers, `plus`
   * for add-an-item editors.
   */
  cellAffordance?: 'chevron' | 'plus'
  /**
   * Render the tags/placeholder as a static display: no popover editor, no
   * add/chevron affordance, no pointer cursor. Overflow expand still works. This is
   * a rendering mode, not a permission — callers gate on their own auth.
   */
  displayOnly?: boolean
  /**
   * How the editor surfaces. `popover` (default) anchors it to the field; `sheet`
   * opens it in a FullScreenPickerSheet that fills the host side panel (matching the
   * resource card's company/user/location field pickers). `sheet` needs a
   * SidePanelOverlay host; it falls back to inline rendering without one.
   */
  presentation?: 'popover' | 'sheet'
  /**
   * Sheet height, ignored unless `presentation="sheet"`. Forwarded to
   * `FullScreenPickerSheet`'s `heightMode` — see {@link SheetHeightMode}.
   */
  sheetHeightMode?: SheetHeightMode
}

const TRIGGER_BASE =
  'tw:relative tw:box-border tw:flex tw:items-center tw:w-full'
const TRIGGER_VARIANT: Record<NonNullable<IProps['variant']>, string> = {
  bordered:
    'tw:border tw:border-solid tw:border-border tw:rounded-lg tw:min-h-8 tw:bg-background',
  cell: 'tw:h-full tw:group/cellpicker',
}

export const PopoverTagPicker: React.FC<IProps> = ({
  isOpen,
  onOpen,
  onClose,
  placeholder,
  triggerLabel,
  popoverContent,
  children,
  leading,
  popoverClassName,
  popoverCollisionAvoidance,
  popoverSide = 'bottom',
  popoverAlign = 'end',
  variant = 'bordered',
  maxVisibleRows,
  cellAffordance,
  displayOnly = false,
  presentation = 'popover',
  sheetHeightMode = 'fill',
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false)
  const [visibleCount, setVisibleCount] = React.useState<number | null>(null)
  const [isOverflowing, setIsOverflowing] = React.useState(false)
  const measureRef = React.useRef<HTMLDivElement>(null)
  const toggleMeasureRef = React.useRef<HTMLButtonElement>(null)
  // The `cell` trigger stretches to the row height for the click target, so on tall
  // rows the popover would open far below the chips — anchor it to the chip row.
  const contentAnchorRef = React.useRef<HTMLDivElement>(null)
  const fieldRef = React.useRef<HTMLDivElement>(null)
  useCloseOnFocusLeavingAnchor(fieldRef, isOpen, onClose)

  const childArray = React.Children.toArray(children)
  // The pinned leading node is always the first child and never collapses; it is
  // measured alongside the tags (so overflow accounts for its width) but excluded
  // from the "+n more" slice. Normalize via React.Children.toArray so a falsy node
  // (e.g. `leading={cond && <Chip/>}` when cond is false) is treated as absent —
  // it must not occupy a child slot or suppress the placeholder.
  const leadingEl =
    React.Children.toArray(leading).length > 0 ? (
      <React.Fragment key="__pinned_leading__">{leading}</React.Fragment>
    ) : null
  const allChildren = leadingEl ? [leadingEl, ...childArray] : childArray
  const childCount = allChildren.length
  const hasChildren = childCount > 0
  const isCollapsible = typeof maxVisibleRows === 'number'

  const measure = React.useCallback(() => {
    const node = measureRef.current
    const toggleNode = toggleMeasureRef.current
    if (!node || !toggleNode || typeof maxVisibleRows !== 'number') {
      setIsOverflowing(false)
      setVisibleCount(null)
      return
    }
    const tagWidths = (Array.from(node.children) as HTMLElement[]).map(
      el => el.offsetWidth,
    )
    const result = computeVisibleTagCount(
      tagWidths,
      node.clientWidth,
      ROW_GAP_PX,
      maxVisibleRows,
      toggleNode.offsetWidth,
    )
    setIsOverflowing(result.isOverflowing)
    setVisibleCount(result.isOverflowing ? result.visibleCount : null)
  }, [maxVisibleRows])

  // Observe the measure node once; childCount changes re-measure (below)
  // without tearing down and recreating the observer on every tag add/remove.
  React.useLayoutEffect(() => {
    const node = measureRef.current
    if (!node || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => measure())
    observer.observe(node)
    return () => observer.disconnect()
  }, [measure])

  // childCount alone misses a leading-chip-for-tag swap (a conditional leading chip
  // can disappear as a tag appears, leaving the count unchanged while the widths
  // change), and the ResizeObserver above only fires when the measure node's own box
  // changes — so track the leading slot's presence too.
  const hasLeading = !!leadingEl
  React.useLayoutEffect(() => {
    measure()
  }, [measure, childCount, hasLeading])

  const isCollapsed = isOverflowing && !isExpanded
  // Never collapse the pinned leading node (index 0) — force it to stay visible
  // even when the measured fit drops to 0 on a very narrow field.
  const clampedVisibleCount =
    visibleCount != null && leadingEl ? Math.max(visibleCount, 1) : visibleCount
  const visibleChildren =
    isCollapsed && clampedVisibleCount != null
      ? allChildren.slice(0, clampedVisibleCount)
      : allChildren
  const hiddenCount =
    isOverflowing && clampedVisibleCount != null
      ? childCount - clampedVisibleCount
      : 0

  const handleOpenChange = (
    nextOpen: boolean,
    details: PopoverPrimitive.Root.ChangeEventDetails,
  ) => {
    // The chips' remove buttons and "+n more" live in the field, beside the trigger.
    if (!nextOpen && isWithinAnchor(details, fieldRef)) {
      return
    }
    if (nextOpen) {
      onOpen()
    } else {
      onClose()
    }
  }

  // Mouse convenience: the whole field opens the picker. Keyboard and assistive tech
  // use the affordance button, which is the real trigger. A press on any button in
  // the field (a chip's remove, "+n more", the trigger itself) is that button's own.
  const handleFieldClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (
      !isEventFromOwnDom(event) ||
      (event.target as Element).closest('button')
    ) {
      return
    }
    if (isOpen) {
      onClose()
    } else {
      onOpen()
    }
  }

  const toggleExpanded = (event: React.MouseEvent) => {
    event.stopPropagation()
    event.preventDefault()
    setIsExpanded(prev => !prev)
  }

  const triggerBody = (
    <div
      className={cn(
        // min-w-0 on every variant, not just `cell`: as a flex child this keeps its
        // default min-width:auto otherwise and grows to the tags' intrinsic width, so the
        // box outgrows the card and the tags' own max-w-full - measured against THIS box -
        // caps them at nothing useful. The cell variant additionally clips.
        'tw:relative tw:py-1 tw:flex-1 tw:min-w-0',
        variant === 'cell'
          ? 'tw:overflow-hidden tw:px-0'
          : displayOnly
            ? 'tw:px-2'
            : 'tw:pl-2 tw:pr-10',
      )}
    >
      {isCollapsible && (
        <div
          aria-hidden
          data-slot="tag-measure-layer"
          className="tw:max-h-0 tw:overflow-hidden tw:invisible tw:pointer-events-none"
        >
          {/* Only the tag children live in measureRef — computeVisibleTagCount
              measures node.children as tag widths and takes the toggle width
              separately, so the toggle must NOT be a measured child here. */}
          <div
            ref={measureRef}
            className="tw:flex tw:flex-wrap tw:gap-1 tw:items-center"
          >
            {allChildren}
          </div>
          <TagOverflowToggle
            ref={toggleMeasureRef}
            isExpanded={false}
            hiddenCount={childCount}
            onToggle={() => undefined}
          />
        </div>
      )}
      <div
        ref={contentAnchorRef}
        className="tw:flex tw:flex-wrap tw:gap-1 tw:items-center"
      >
        {!hasChildren && (
          <span className="tw:text-body-md tw:text-muted-foreground tw:leading-[20px]">
            {placeholder}
          </span>
        )}
        {visibleChildren}
        {isOverflowing && (
          <TagOverflowToggle
            isExpanded={isExpanded}
            hiddenCount={hiddenCount}
            onToggle={toggleExpanded}
          />
        )}
      </div>
    </div>
  )

  if (displayOnly) {
    return (
      <div
        className={cn(
          TRIGGER_BASE,
          'tw:cursor-default',
          TRIGGER_VARIANT[variant],
        )}
      >
        {triggerBody}
      </div>
    )
  }

  const isSheet = presentation === 'sheet'
  const affordanceButton = (
    <Button
      variant="ghost"
      size={variant === 'cell' ? 'icon-xs' : 'icon-sm'}
      aria-label={triggerLabel}
      onClick={isSheet ? onOpen : undefined}
    >
      {cellAffordance === 'plus' ? <Plus /> : <ChevronDown />}
    </Button>
  )
  const affordanceControl = isSheet ? (
    affordanceButton
  ) : (
    <PopoverTrigger render={affordanceButton} />
  )
  const affordance =
    variant === 'bordered' ? (
      <div className="tw:absolute tw:right-0 tw:top-1/2 tw:flex tw:-translate-y-1/2 tw:items-center tw:justify-center tw:pr-0.5 tw:text-muted-foreground">
        {affordanceControl}
      </div>
    ) : (
      <span
        className={cn(
          'tw:-mr-1 tw:flex tw:shrink-0 tw:text-muted-foreground',
          !isOpen &&
            'tw:opacity-0 tw:group-hover/cellpicker:opacity-100 tw:focus-within:opacity-100',
        )}
      >
        {affordanceControl}
      </span>
    )

  const field = (
    <div
      ref={fieldRef}
      className={cn(
        TRIGGER_BASE,
        'tw:cursor-pointer',
        TRIGGER_VARIANT[variant],
      )}
      onClick={handleFieldClick}
    >
      {triggerBody}
      {affordance}
    </div>
  )

  if (isSheet) {
    return (
      <>
        {field}
        <FullScreenPickerSheet
          isOpen={isOpen}
          onClose={onClose}
          heightMode={sheetHeightMode}
        >
          {popoverContent}
        </FullScreenPickerSheet>
      </>
    )
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      {field}
      <PopoverContent
        side={popoverSide}
        align={popoverAlign}
        collisionAvoidance={popoverCollisionAvoidance}
        // The `cell` field stretches to the row height, so anchor to the chip row
        // there; the bordered field is its own anchor.
        anchor={variant === 'cell' ? contentAnchorRef : fieldRef}
        className={cn({
          'tw:w-auto tw:p-0 tw:flex tw:flex-col tw:overflow-hidden': true,
          [popoverClassName]: !!popoverClassName,
        })}
      >
        {popoverContent}
      </PopoverContent>
    </Popover>
  )
}
